import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import PitBoardLogo from "@/components/PitBoardLogo";
import {
  Settings, BookOpen, Users, FileText, CreditCard, Clipboard, TrendingUp,
  LogOut, LayoutDashboard, ShoppingCart, Building2, Boxes, ChevronDown, Palette, Crown, ShoppingBag, ArrowDownToLine, Shield, Menu, X
} from "lucide-react";

/**
 * Sidebar groupée en sections collapsibles + scrollable :
 *   • Pilotage      (Tableau de bord, Trésorerie)
 *   • Entreprise    (Profil entreprise, Charges fixes)
 *   • Catalogue     (Matériaux · Articles · MO, Stock, Clients)
 *   • Production    (Devis, Factures, Matériel à commander, Fiches chantier)
 */
const NAV_GROUPS = [
  {
    key: "pilotage",
    label: "Pilotage",
    items: [
      { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-home", end: true },
      { to: "/encaissements", label: "Encaissements", icon: ArrowDownToLine, testid: "nav-incomes" },
      { to: "/treasury", label: "Dépenses", icon: TrendingUp, testid: "nav-treasury" },
    ],
  },
  {
    key: "entreprise",
    label: "Entreprise",
    items: [
      { to: "/profile", label: "Profil entreprise", icon: Building2, testid: "nav-profile" },
      { to: "/setup", label: "Charges fixes mensuelles", icon: Settings, testid: "nav-setup" },
      { to: "/equipe", label: "Équipe", icon: Users, testid: "nav-team" },
    ],
  },
  {
    key: "abonnement",
    label: "Abonnement",
    items: [
      { to: "/abonnement", label: "Mon abonnement", icon: CreditCard, testid: "nav-subscription" },
      { to: "/packs", label: "Packs Métiers", icon: ShoppingBag, testid: "nav-packs" },
    ],
  },
  {
    key: "platform_admin",
    label: "Éditeur PitBoard",
    platformAdminOnly: true,
    items: [
      { to: "/admin/plateforme", label: "Dashboard SaaS", icon: Crown, testid: "nav-platform-admin", platformAdminOnly: true },
      { to: "/admin/allowlist", label: "Beta Allowlist", icon: Shield, testid: "nav-allowlist-admin", platformAdminOnly: true },
      { to: "/brand", label: "Logos & charte", icon: Palette, testid: "nav-brand", platformAdminOnly: true },
    ],
  },
  {
    key: "catalogue",
    label: "Catalogue",
    items: [
      { to: "/library", label: "Matériaux · Articles · MO", icon: BookOpen, testid: "nav-library" },
      { to: "/stock", label: "Stock (Articles + Matériaux)", icon: Boxes, testid: "nav-stock" },
      { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
    ],
  },
  {
    key: "production",
    label: "Production",
    items: [
      { to: "/quotes", label: "Devis", icon: FileText, testid: "nav-quotes" },
      { to: "/invoices", label: "Factures", icon: CreditCard, testid: "nav-invoices" },
      { to: "/material-lists", label: "Matériel à commander", icon: ShoppingCart, testid: "nav-material-lists", pulseSource: "pending", btpOnly: true },
      { to: "/site-orders", label: "Fiches chantier", icon: Clipboard, testid: "nav-site-orders", btpOnly: true },
    ],
  },
];

const STORAGE_KEY = "pitboard_sidebar_groups_v1";

export default function AppLayout({ children }) {
  const { user, company, logout } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [onboarding, setOnboarding] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* noop */ }
    // Par défaut : tous les groupes ouverts
    return Object.fromEntries(NAV_GROUPS.map((g) => [g.key, true]));
  });

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  useEffect(() => {
    let alive = true;
    const fetchPending = async () => {
      try {
        const { data } = await api.get("/material-lists/pending-count");
        if (alive) setPendingCount(data?.pending_items || 0);
      } catch { /* ignore */ }
    };
    const fetchOnboarding = async () => {
      try {
        const { data } = await api.get("/onboarding/status");
        if (alive) setOnboarding(data);
      } catch { /* ignore */ }
    };
    const fetchSub = async () => {
      try {
        const { data } = await api.get("/billing/me");
        if (alive) setSubscription(data);
      } catch { /* ignore */ }
    };
    fetchPending();
    fetchOnboarding();
    fetchSub();
    const id = setInterval(() => { fetchPending(); fetchOnboarding(); fetchSub(); }, 30000);
    const onUpd = () => { fetchPending(); fetchOnboarding(); fetchSub(); };
    window.addEventListener("ml:updated", onUpd);
    window.addEventListener("onboarding:refresh", onUpd);
    return () => { alive = false; clearInterval(id); window.removeEventListener("ml:updated", onUpd); window.removeEventListener("onboarding:refresh", onUpd); };
  }, []);

  const isPrestation = (company?.business_mode || "complet") === "prestation";

  // Filtrer les groupes selon le mode entreprise + flatten pour mobile bottom nav
  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((m) => {
      if (isPrestation && m.btpOnly) return false;
      if (m.platformAdminOnly && !user?.is_platform_admin) return false;
      return true;
    }),
  })).filter((g) => {
    if (g.platformAdminOnly && !user?.is_platform_admin) return false;
    return g.items.length > 0;
  });

  const allFlatItems = visibleGroups.flatMap((g) => g.items);

  // Mobile bottom nav — 4 raccourcis les plus utilisés sur chantier + bouton "Plus"
  // Ordre : Dashboard, Devis, Factures, Clients (les 4 essentiels d'un artisan en déplacement)
  const MOBILE_PRIMARY_PATHS = ["/", "/quotes", "/invoices", "/clients"];
  const mobilePrimary = MOBILE_PRIMARY_PATHS
    .map((path) => allFlatItems.find((i) => i.to === path))
    .filter(Boolean);

  return (
    <div className="flex min-h-screen bg-app">
      {/* Sidebar (desktop) */}
      <aside className="sidebar" data-testid="sidebar">
        <div className="flex items-center gap-3 px-2 mb-6 shrink-0">
          <PitBoardLogo size={56} />
        </div>

        {onboarding && !onboarding.all_done && (
          <NavLink
            to="/"
            className="mb-4 mx-1 p-3 rounded-lg block group shrink-0"
            style={{ background: "rgba(255, 196, 0, 0.12)", border: "1px solid rgba(255, 196, 0, 0.3)" }}
            data-testid="onboarding-sidebar-badge"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-[11px] font-semibold" style={{ color: "#FFC400" }}>Mise en route</span>
              <span className="numerals text-[11px]" style={{ color: "var(--chalk)" }}>
                {onboarding.completed}/{onboarding.total}
              </span>
            </div>
            <div className="h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,252,246,0.08)" }}>
              <div className="h-full transition-all" style={{ width: `${(onboarding.completed / onboarding.total) * 100}%`, background: "#FFC400" }} />
            </div>
            <div className="text-[11px] mt-2 font-body group-hover:underline" style={{ color: "var(--ink-soft)" }}>
              Étape {onboarding.next_step_index} : continuer →
            </div>
          </NavLink>
        )}

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto pr-1 -mr-1 flex flex-col gap-3" data-testid="sidebar-nav">
          {visibleGroups.map((g) => {
            const isOpen = openGroups[g.key] ?? true;
            // Couleur d'accent par groupe (cohérent avec le branding PitBoard)
            const groupColor = {
              pilotage:         "#FFC400",  // jaune signature — usage quotidien
              entreprise:       "#74C0FC",  // bleu clair — admin
              abonnement:       "#E10600",  // rouge brand — billing / premium
              editeur_pitboard: "#B197FC",  // violet — admin plateforme
              editeur:          "#B197FC",
              catalogue:        "#FFD43B",  // jaune doré — référentiel
              production:       "#51CF66",  // vert — facturation/CA
            }[g.key] || "var(--grey)";
            return (
              <div key={g.key} className="flex flex-col gap-0.5">
                <button
                  onClick={() => toggleGroup(g.key)}
                  className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition-colors"
                  style={{ color: groupColor }}
                  data-testid={`sidebar-group-${g.key}`}
                >
                  <ChevronDown
                    size={12}
                    strokeWidth={2.5}
                    style={{ transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.15s" }}
                  />
                  <span style={{ letterSpacing: "0.08em" }}>{g.label}</span>
                </button>
                {isOpen && g.items.map(({ to, label, icon: Icon, testid, end, pulseSource }) => {
                  const pulse = pulseSource === "pending" && pendingCount > 0;
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      data-testid={testid}
                      end={end}
                      className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                    >
                      <Icon size={16} strokeWidth={1.7} />
                      <span className="flex-1">{label}</span>
                      {pulse && (
                        <span className="pulse-dot" data-testid="pending-badge" title={`${pendingCount} matériau(x) à commander`}>
                          {pendingCount > 99 ? "99+" : pendingCount}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-white/10 pt-4 mt-4 shrink-0">
          <div className="px-2 mb-3">
            <div className="text-sm font-medium text-[var(--chalk)] truncate">{company?.name || "Mon entreprise"}</div>
            <div className="text-xs text-[var(--grey)] truncate">{user?.email}</div>
          </div>
          <button onClick={logout} className="sidebar-link w-full text-left" data-testid="logout-btn">
            <LogOut size={16} strokeWidth={1.7} /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main content + mobile top bar */}
      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden px-4 py-3 flex items-center justify-between sticky top-0 z-40" style={{ background: "var(--carbon)", borderBottom: "1px solid var(--steel)" }}>
          <PitBoardLogo size={44} />
          <button onClick={logout} className="btn-ghost btn btn-sm" data-testid="mobile-logout">
            <LogOut size={14} />
          </button>
        </div>

        <div className="p-4 sm:p-6 lg:p-10 pb-24 md:pb-10">
          {subscription && (subscription.status === "expired" || (subscription.is_active && subscription.days_remaining <= 5)) && (
            <NavLink
              to="/abonnement"
              data-testid="sub-banner"
              className="block mb-5 px-4 py-3 rounded-lg border text-sm hover:opacity-90 transition-opacity"
              style={{
                background: subscription.status === "expired" ? "rgba(225,6,0,0.10)" : "rgba(255,196,0,0.10)",
                borderColor: subscription.status === "expired" ? "rgba(225,6,0,0.40)" : "rgba(255,196,0,0.40)",
              }}
            >
              <span className="font-semibold" style={{ color: subscription.status === "expired" ? "#E10600" : "#FFC400" }}>
                {subscription.status === "expired"
                  ? "Abonnement expiré"
                  : subscription.status === "trialing"
                    ? `Essai gratuit — ${subscription.days_remaining} jour${subscription.days_remaining > 1 ? "s" : ""} restant${subscription.days_remaining > 1 ? "s" : ""}`
                    : `Abonnement à renouveler dans ${subscription.days_remaining} jour${subscription.days_remaining > 1 ? "s" : ""}`}
              </span>
              <span style={{ color: "var(--chalk)" }}> — Cliquez ici pour {subscription.status === "expired" ? "réactiver" : "renouveler"} (19 € HT/mois · 22,80 € TTC).</span>
            </NavLink>
          )}
          {children}
        </div>

        {/* Mobile bottom nav — 4 raccourcis clés + bouton "Plus" (accès complet) */}
        <div className="bottom-nav" data-testid="bottom-nav">
          {mobilePrimary.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `bottom-nav-link ${isActive ? "active" : ""}`}
              data-testid={`mnav-${testid || to}`}
            >
              <Icon size={22} strokeWidth={1.8} />
              <span>{label.split(" ")[0]}</span>
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="bottom-nav-link"
            data-testid="mnav-more"
            aria-label="Ouvrir le menu complet"
          >
            <Menu size={22} strokeWidth={1.8} />
            <span>Plus</span>
          </button>
        </div>

        {/* Mobile menu drawer — accès à tous les items */}
        {mobileMenuOpen && (
          <div
            className="mobile-menu-backdrop"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-menu-backdrop"
          >
            <div
              className="mobile-menu-panel"
              onClick={(e) => e.stopPropagation()}
              data-testid="mobile-menu-panel"
            >
              <div className="mobile-menu-header">
                <div className="flex items-center gap-2">
                  <PitBoardLogo size={36} />
                  <span className="font-display text-lg" style={{ fontWeight: 700, color: "var(--chalk)" }}>Menu</span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="btn btn-ghost"
                  style={{ padding: "6px 10px", minHeight: 36 }}
                  data-testid="mobile-menu-close"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="mobile-menu-content">
                {visibleGroups.map((g) => (
                  <div key={g.key} className="mobile-menu-group">
                    <div className="mobile-menu-group-label">{g.label}</div>
                    {g.items.map(({ to, label, icon: Icon, end, testid, pulseSource }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={end}
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) => `mobile-menu-link ${isActive ? "active" : ""}`}
                        data-testid={`mmenu-${testid || to}`}
                      >
                        <Icon size={18} strokeWidth={1.8} />
                        <span className="flex-1">{label}</span>
                        {pulseSource === "pending" && pendingCount > 0 && (
                          <span className="badge badge-accent" style={{ fontSize: "10px", padding: "2px 6px" }}>{pendingCount}</span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                ))}
                <button
                  onClick={() => { setMobileMenuOpen(false); logout(); }}
                  className="mobile-menu-link"
                  style={{ color: "#E10600", marginTop: 8 }}
                  data-testid="mobile-menu-logout"
                >
                  <LogOut size={18} strokeWidth={1.8} />
                  <span>Se déconnecter</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
